import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AdminShell } from "./AdminShell";
import { PageHeader } from "./parts";

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => toast.error("Impossible de charger les paramètres."));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        app_name: s.app_name, logo_url: s.logo_url, sender_email: s.sender_email, sender_name: s.sender_name,
        password_min_length: Number(s.password_min_length), password_require_letter_digit: s.password_require_letter_digit,
        session_hours: Number(s.session_hours),
      };
      const res = await api.put("/admin/settings", payload);
      setS(res.data);
      toast.success("Paramètres enregistrés.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <AdminShell>
      <PageHeader
        title="Paramètres de la plateforme"
        subtitle="Configuration générale."
        action={s && <Button onClick={save} disabled={saving} data-testid="settings-save-btn">{saving ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Save size={16} className="mr-1.5" />} Enregistrer</Button>}
      />
      {!s ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="settings-form">
          <div className="space-y-5 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h3 className="text-sm font-semibold text-slate-900">Identité</h3>
            <Field label="Nom de l'application"><Input value={s.app_name || ""} onChange={(e) => setS({ ...s, app_name: e.target.value })} data-testid="setting-app-name" /></Field>
            <Field label="URL du logo" hint="Lien vers une image (facultatif)."><Input value={s.logo_url || ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })} data-testid="setting-logo-url" /></Field>
          </div>

          <div className="space-y-5 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h3 className="text-sm font-semibold text-slate-900">E-mails</h3>
            <Field label="Nom de l'expéditeur"><Input value={s.sender_name || ""} onChange={(e) => setS({ ...s, sender_name: e.target.value })} data-testid="setting-sender-name" /></Field>
            <Field label="E-mail de l'expéditeur"><Input value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })} data-testid="setting-sender-email" /></Field>
          </div>

          <div className="space-y-5 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h3 className="text-sm font-semibold text-slate-900">Sécurité</h3>
            <Field label="Longueur minimale du mot de passe"><Input type="number" min={6} max={64} value={s.password_min_length ?? 8} onChange={(e) => setS({ ...s, password_min_length: e.target.value })} data-testid="setting-pw-length" /></Field>
            <div className="flex items-center justify-between">
              <div><Label>Exiger lettre + chiffre</Label><p className="text-xs text-slate-400">Renforce la robustesse des mots de passe.</p></div>
              <Switch checked={!!s.password_require_letter_digit} onCheckedChange={(v) => setS({ ...s, password_require_letter_digit: v })} data-testid="setting-pw-complexity" />
            </div>
            <Field label="Durée de session (heures)"><Input type="number" min={1} max={168} value={s.session_hours ?? 12} onChange={(e) => setS({ ...s, session_hours: e.target.value })} data-testid="setting-session-hours" /></Field>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
